import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useProjects } from "../lib/ProjectContext";
import { ANSWER_CACHED_EVENT, readCache } from "../lib/answerCache";
import { api } from "../lib/apiClient";
import { formatRelativeTimeCompact } from "../lib/formatRelativeTime";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { UserMenu } from "./UserMenu";
import type { IdentityUser } from "../lib/identity";
import type { AuthedUser } from "../types/authedUser";
import type { ChatThread, Role } from "@shared/types";

const COLLAPSED_KEY = "fieldbook:railCollapsed";
const MAX_RECENT_CHATS = 5;

function navClass({ isActive }: { isActive: boolean }) { return isActive ? "rail-link active" : "rail-link"; }

const Icon = ({ children }: { children: React.ReactNode }) => <svg width="16" height="16" viewBox="0 0 16 16" fill="none">{children}</svg>;

export function Rail({
  user,
  appUser,
  role,
  onLogout,
}: {
  user: IdentityUser;
  appUser: AuthedUser | null;
  role: Role | null;
  onLogout: () => void;
}) {
  const displayName = appUser?.fullName || user.email;
  const { selectedProjectId } = useProjects();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    if (stored !== null) return stored === "1";
    return typeof window !== "undefined" && window.innerWidth <= 860;
  });
  const [recentChats, setRecentChats] = useState<ChatThread[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.style.setProperty("--rail-w", collapsed ? "64px" : "240px");
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    if (!selectedProjectId) { setRecentChats([]); return; }
    setRecentChats(readCache(user.id, selectedProjectId).slice(0, MAX_RECENT_CHATS));
    let cancelled = false;
    function fetchHistory() {
      api.get<{ threads: ChatThread[] }>(`/api/projects/${selectedProjectId}/ask-history`)
        .then((data) => { if (!cancelled) setRecentChats(data.threads.slice(0, MAX_RECENT_CHATS)); })
        .catch(() => {});
    }
    fetchHistory();
    function onAnswerCached(e: Event) {
      const detail = (e as CustomEvent<{ projectId: string }>).detail;
      if (detail?.projectId === selectedProjectId) fetchHistory();
    }
    window.addEventListener(ANSWER_CACHED_EVENT, onAnswerCached);
    return () => { cancelled = true; window.removeEventListener(ANSWER_CACHED_EVENT, onAnswerCached); };
  }, [selectedProjectId, user.id]);

  function closeOnMobile() { if (window.innerWidth <= 860) setCollapsed(true); }
  function handleNewChat() { setActiveChatId(null); navigate("/ask", { state: { newChatAt: Date.now() } }); closeOnMobile(); }
  function handleOpenChat(entry: (typeof recentChats)[number]) {
    setActiveChatId(entry.threadId);
    navigate("/ask", { state: { restoreThread: { threadId: entry.threadId, turns: entry.turns } } });
    closeOnMobile();
  }

  return (
    <aside className={collapsed ? "rail collapsed" : "rail"}>
      <div className="rail-top">
        <div className="rail-brand"><div className="rail-mark">N</div><div className="rail-brand-text hide-on-collapse"><span className="rail-eyebrow">CEI</span><span className="rail-wordmark">Nexus</span></div></div>
        <button className="rail-collapse-btn" type="button" title={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} onClick={() => setCollapsed((c) => !c)}>
          <Icon><path d="M5.5 3.5 10.5 8l-5 4.5M2.5 3.5v9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></Icon>
        </button>
      </div>
      <div className="hide-on-collapse"><ProjectSwitcher /></div>
      <button className="new-chat-btn" type="button" title="New chat" onClick={handleNewChat}><Icon><path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></Icon><span>New chat</span></button>

      <div className="rail-scroll">
        <nav className="rail-nav" aria-label="Main" onClick={closeOnMobile}>
          <span className="rail-nav-label hide-on-collapse">Overview</span>
          <NavLink to="/" end className={navClass} title="Dashboard"><Icon><rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/><rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/><rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/><rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Dashboard</span></NavLink>

          <span className="rail-nav-label hide-on-collapse" style={{ marginTop: ".5rem" }}>Assist</span>
          <NavLink to="/ask" className={navClass} title="CEI Nexus Assist"><Icon><path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v6A1.5 1.5 0 0 1 12.5 11H8l-3 3v-3H3.5A1.5 1.5 0 0 1 2 9.5v-6Z" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Assist</span></NavLink>
          <NavLink to="/escalations" className={navClass} title="Expert Questions"><Icon><circle cx="5.5" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.3"/><path d="M1.5 13c.3-2 1.9-3.5 4-3.5s3.7 1.5 4 3.5" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Expert Questions</span></NavLink>
          {role === "owner" && <NavLink to="/sources" className={navClass} title="Knowledge Library"><Icon><path d="M3 2.5h7l3 3v8a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5Z" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Sources</span></NavLink>}

          <span className="rail-nav-label hide-on-collapse" style={{ marginTop: ".5rem" }}>Project</span>
          <NavLink to="/contract-docs" className={navClass} title="Project Documents"><Icon><path d="M3 2.5h6.5l3.5 3.5V13a.5.5 0 0 1-.5.5h-9A.5.5 0 0 1 3 13V2.5Z" stroke="currentColor" strokeWidth="1.3"/><path d="M9.5 2.5V6h3.5" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Project Docs</span></NavLink>
          {role === "owner" && <NavLink to="/upload" className={navClass} title="Upload"><Icon><path d="M8 2.5v7M8 2.5 5 5.5M8 2.5 11 5.5M3 10v2.5A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5V10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></Icon><span>Upload</span></NavLink>}
          {role === "owner" && <NavLink to="/review" className={navClass} title="Review"><Icon><path d="M3.5 8.5 6.5 11.5 12.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></Icon><span>Review</span></NavLink>}

          <span className="rail-nav-label hide-on-collapse" style={{ marginTop: ".5rem" }}>Connect</span>
          <NavLink to="/network" className={navClass} title="CEI Nexus Connect"><Icon><circle cx="4.5" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.3"/><circle cx="11.5" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.3"/><circle cx="8" cy="11.5" r="2" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Connect</span></NavLink>
          <NavLink to="/messages" className={navClass} title="Messages"><Icon><rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M2 4l6 4.5L14 4" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Messages</span></NavLink>

          <span className="rail-nav-label hide-on-collapse" style={{ marginTop: ".5rem" }}>Other</span>
          {role === "owner" && <NavLink to="/admin/answer-quality" className={navClass} title="Answer Quality"><Icon><path d="M2.5 12.5V8.8M6.2 12.5V5.8M9.8 12.5V3.5M13.5 12.5V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="m2.5 6.3 3.7-2 3.6 1.2 3.7-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></Icon><span>Answer Quality</span></NavLink>}
          {role === "owner" && <NavLink to="/projects/new" className={navClass} title="New project"><Icon><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></Icon><span>New project</span></NavLink>}
          {role === "owner" && <NavLink to="/admin/users" className={navClass} title="Users"><Icon><circle cx="6" cy="5.5" r="2.3" stroke="currentColor" strokeWidth="1.3"/><path d="M1.8 13c.3-2.2 2-3.8 4.2-3.8s3.9 1.6 4.2 3.8" stroke="currentColor" strokeWidth="1.3"/></Icon><span>Users</span></NavLink>}
          <NavLink to="/settings" className={navClass} title="Settings"><Icon><circle cx="8" cy="8" r="2.3" stroke="currentColor" strokeWidth="1.3"/><path d="M8 2v1.6M8 12.4V14M14 8h-1.6M3.6 8H2M12.1 3.9l-1.1 1.1M5 10l-1.1 1.1M12.1 12.1 11 11M5 6 3.9 4.9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></Icon><span>Settings</span></NavLink>
        </nav>

        <div className="rail-chats hide-on-collapse">
          <span className="rail-nav-label">Recent chats</span>
          {recentChats.length === 0 ? <span className="rail-chats-empty">Nothing asked yet</span> : recentChats.map((entry) => (
            <button key={entry.threadId} className={entry.threadId === activeChatId ? "chat-item active-chat" : "chat-item"} type="button" onClick={() => handleOpenChat(entry)} title={entry.turns[0]?.question}>
              <span className="chat-item-text">{entry.turns[0]?.question}</span><span className="chat-item-time">{formatRelativeTimeCompact(entry.cachedAt)}</span>
            </button>
          ))}
          {recentChats.length > 0 && (
            <NavLink to="/chats" className="chat-item chat-item-view-all" onClick={closeOnMobile}>
              <span className="chat-item-text">View all chats</span>
            </NavLink>
          )}
        </div>
      </div>
      <div className="rail-user">
        <UserMenu user={user} appUser={appUser} role={role} onLogout={onLogout} />
        <div className="rail-user-meta hide-on-collapse">
          <span className="rail-user-name">{displayName}</span>
          <span className="rail-user-role">{role ? role.replace(/_/g, " ") : ""}</span>
        </div>
      </div>
    </aside>
  );
}
